/**
 * Determines the appropriate rounding increment for the Y-axis min and max based on the value range.
 *
 * This function calculates the range between the minimum and maximum values, and returns a
 * rounding increment based on predefined thresholds. The larger the range, the larger the increment:
 * - For ranges greater than or equal to 4000, it returns 1000.
 * - For ranges between 2000 and 3999, it returns 500.
 * - For ranges smaller than 2000, it returns 100.
 *
 * @param {Object} params - The parameters for determining the rounding increment.
 * @param {number} params.minVal - The minimum value on the Y-axis.
 * @param {number} params.maxVal - The maximum value on the Y-axis.
 *
 * @returns {number} The rounding increment to use for the Y-axis.
 */
export function getYAxisRoundingIncrementForMinAndMax({ minVal, maxVal }) {
  const range = maxVal - minVal;
  if (range >= 4000) {
    return 1000;
  }
  if (range >= 2000) {
    return 500;
  }

  return 100;
}

export const ROUND_UP = 'ROUND_UP';
export const ROUND = 'ROUND';
export const ROUND_DOWN = 'ROUND_DOWN';

/**
 * Rounds a given value to the nearest specified increment using the specified rounding type.
 * It supports three rounding types: rounding up, rounding down, and standard rounding.
 *
 * @param {Object} params - The parameters for rounding.
 * @param {number} params.value - The value to be rounded.
 * @param {number} params.nearestIncrement - The increment to which the value should be rounded.
 * @param {string} params.roundingType - The type of rounding to apply. Can be one of:
 *   - `ROUND_UP` (rounds the value up)
 *   - `ROUND_DOWN` (rounds the value down)
 *   - `ROUND` (standard rounding, rounds to the nearest value)
 *
 * @returns {number} The rounded value.
 */
export function roundToNearestIncrement({ value, nearestIncrement, roundingType }) {
  const factor = 1 / nearestIncrement;
  switch (roundingType) {
    case ROUND_UP:
      return Math.ceil(value * factor) / factor;
    case ROUND_DOWN:
      return Math.floor(value * factor) / factor;
    case ROUND:
    default:
      return Math.round(value * factor) / factor;
  }
}

/**
 * Scales and rounds a given value to a specified decimal place and rounding increment.
 *
 * This function first rounds the input value to the nearest specified increment,
 * scales the value by shifting its decimal place, and then rounds it again
 * to the specified decimal precision.
 *
 * @param {Object} params - The parameters for scaling and rounding.
 * @param {number} params.value - The value to be scaled and rounded.
 * @param {number} [params.decimalPlacement=1000] - The scaling factor for shifting the decimal place.
 * @param {number} [params.roundToNearest=100] - The increment to which the value should first be rounded.
 * @param {number} [params.decimalPrecision=1] - The number of decimal places to round the final result to.
 *
 * @returns {number} The scaled and rounded value.
 */
export function scaleAndRound({ value, decimalPlacement = 1000, roundToNearest = 100, decimalPrecision = 1 }) {
  const roundedValue = roundToNearestIncrement({ value, nearestIncrement: roundToNearest, roundingType: ROUND });
  const shiftedValue = roundedValue / decimalPlacement;

  return roundToNearestIncrement({
    value: shiftedValue,
    nearestIncrement: 1 / Math.pow(10, decimalPrecision),
    roundingType: ROUND,
  });
}

/**
 * Formats a price as a string in thousands (k) with a dollar sign, rounded to the nearest hundred. (e.g., "$2.5k").
 *
 * @param {number} price - The price value to format.
 *
 * @returns {string} The formatted price string in thousands with a dollar sign (e.g., "$2.5k").
 */
export function formatPriceInThousands(price) {
  return `$${scaleAndRound({ value: price, decimalPlacement: 1000, roundToNearest: 50, decimalPrecision: 2 })}k`;
}
